import { Space, Tooltip } from "antd";
import Text from "antd/es/typography/Text";
import dayjs from "dayjs";
import { FaEye } from "react-icons/fa6";
import { TFunction } from "i18next";
import { clientEntities } from "@neonumy/api-clients";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import { DATE_FORMAT } from "config/settings";
import { formatNumberWithSpaces } from "utils/formatNumberWithSpaces";
import { customToFixed } from "utils/toFixedNumber";
import TooltipButton from "views/Documents/components/TooltipButton";

interface GetColumnsProps {
  t: TFunction;
  companyId: ID;
  handleViewPdf: (id: ID) => void;
  loadingPdf: boolean;
}

export const getColumns = ({ t, companyId, handleViewPdf, loadingPdf }: GetColumnsProps) => [
  {
    title: t("Reference"),
    dataIndex: "reference",
    key: "reference",
    sorter: true,
    filterType: {
      type: "SearchFilter",
      varName: "reference_Startswith",
    },
    render: (value: string) => {
      return (
        <Space size="small" direction="vertical">
          <Text>{value}</Text>
        </Space>
      );
    },
  },
  {
    title: t("Client"),
    dataIndex: "client",
    key: "client",
    width: "15%",
    filterType: {
      type: "AsyncSelectFilter",
      varName: "clientId_In",
      asyncSelectSettings: {
        hasQueryFilter: true,
        isMulti: true,
        returnSelectedObject: true,
        autoConfirm: false,
        queryOptions: {
          client: clientEntities,
          dataName: "listEntities",
          query: LIST_CLIENTS_PAGINATED_SELECT,
          variables: {
            filter: {
              companyId,
              entityType: "CLIENT",
            },
            limit: 20,
            orderBy: ["name"],
          },
        },
        translations: {
          placeholder: t(""),
          loadingMessage: t("Loading"),
          noOptionsMessage: t("No options"),
          button: {
            search: t("Search"),
            reset: t("Reset"),
          },
        },
      },
    },
    render: (value: { name?: string }) => {
      return (
        <Space size="small" direction="vertical">
          <Tooltip title={value?.name || ""}>
            <Text ellipsis>{`${value?.name || ""}`}</Text>
          </Tooltip>
        </Space>
      );
    },
  },
  {
    title: t("Date"),
    sorter: true,
    key: "date",
    filterType: {
      type: "DateFilter",
      varName: ["date_Gte", "date_Lte"],
    },
    render: (record: string) => {
      return (
        <Space size="small" direction="vertical">
          <Text>{dayjs(record).format(DATE_FORMAT)}</Text>
        </Space>
      );
    },
  },
  {
    title: t("Total"),
    dataIndex: "totalDefaultCurrency",
    key: "totalDefaultCurrency",
    align: "left",
    render: (value: number, record: ListReceiptsData) => {
      const decimalPlaces = record?.decimalPlaces;
      const currencyCode = record?.currencyCode;
      return (
        <Space size="small" direction="vertical">
          <Text>
            {formatNumberWithSpaces(customToFixed(Number(value), decimalPlaces))} {currencyCode}
          </Text>
        </Space>
      );
    },
  },
  {
    title: t("Action"),
    key: "action",
    align: "center" as const,
    width: "100px",
    render: (_text: string, record: ListReceiptsData) => {
      return (
        <Space size="small" direction="horizontal" className="centered">
          <TooltipButton
            loading={loadingPdf}
            placement="top"
            title={t("View receipt")}
            handleOnClick={() => handleViewPdf(record.id)}
            icon={<FaEye />}
          />
        </Space>
      );
    },
  },
];
